import { API_URL_PATIENT_PORTRAIT } from '../../config/Environment';
import {
	getNakamaFetchClient,
	type DeepReadonly,
	type GetRequestPathParams,
	type GetRequestQueryParams,
	type GetResponseBodyJson,
} from '../openapiFetchClient';
import { getMemoizedApiMethodByPatientIdOnce } from './PatientPortraitApi.utils';

import type { paths as MutablePatientPortraitPaths } from './PatientPortraitApi.types.generated';

type PatientPortraitPaths = DeepReadonly<MutablePatientPortraitPaths>;
type NakamaFetchClient = ReturnType<typeof getNakamaFetchClient<PatientPortraitPaths>>;

const GET_FEATURE_FLAG_PATIENT_CONTEXT_PATH = '/v1/product-context/feature-flag/patient/{patientId}';
export type GetFeatureFlagPatientContextRequest = GetRequestPathParams<
	PatientPortraitPaths,
	typeof GET_FEATURE_FLAG_PATIENT_CONTEXT_PATH
>;
export type GetFeatureFlagPatientContextResponse = Omit<
	GetResponseBodyJson<PatientPortraitPaths, typeof GET_FEATURE_FLAG_PATIENT_CONTEXT_PATH>,
	'navinaPatientId'
>;

const GET_ALLOWED_TABS_PATH = '/v1/product-context/allowed-tabs';
type GetAllowedTabsQueryParams = GetRequestQueryParams<PatientPortraitPaths, typeof GET_ALLOWED_TABS_PATH>;
export type GetAllowedTabsRequest = GetAllowedTabsQueryParams;
export type GetAllowedTabsResponse = GetResponseBodyJson<PatientPortraitPaths, typeof GET_ALLOWED_TABS_PATH>;

const GET_ALLOWED_TABS_MOCK_PATH = '/v1/product-context/allowed-tabs/mock';
type GetAllowedTabsMockQueryParams = GetRequestQueryParams<PatientPortraitPaths, typeof GET_ALLOWED_TABS_MOCK_PATH>;
export type GetAllowedTabsMockResponse = GetResponseBodyJson<PatientPortraitPaths, typeof GET_ALLOWED_TABS_MOCK_PATH>;

interface ProductContextApiProps {
	readonly client?: NakamaFetchClient;
}

const NAKAMA_SERVICE_NAME = 'nakama-patient-portrait';

export class ProductContextApi {
	private readonly client: NakamaFetchClient;

	private constructor({
		client = getNakamaFetchClient<PatientPortraitPaths>({
			baseUrl: API_URL_PATIENT_PORTRAIT,
			nakamaServiceName: NAKAMA_SERVICE_NAME,
		}),
	}: ProductContextApiProps) {
		this.client = client;
	}

	static readonly create = (props: ProductContextApiProps) => new ProductContextApi(props);

	readonly getFeatureFlagPatientContext = async (
		requestPayload: GetFeatureFlagPatientContextRequest,
	): Promise<GetFeatureFlagPatientContextResponse> => {
		const response = await this.client.GET(GET_FEATURE_FLAG_PATIENT_CONTEXT_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getAllowedTabs = async (requestPayload: GetAllowedTabsQueryParams): Promise<GetAllowedTabsResponse> => {
		const { patientId, appointmentId } = requestPayload;

		const response = await this.client.GET(GET_ALLOWED_TABS_PATH, {
			params: {
				query: { patientId, appointmentId },
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getAllowedTabsMock = async (
		requestPayload: GetAllowedTabsMockQueryParams,
	): Promise<GetAllowedTabsMockResponse> => {
		const { patientId, appointmentId } = requestPayload;

		const response = await this.client.GET(GET_ALLOWED_TABS_MOCK_PATH, {
			params: {
				query: { patientId, appointmentId },
			},
		});

		const { data } = response;

		if (!data) {
			throw new Error('Failed to fetch allowed tabs mock');
		}

		return data;
	};
}

let maybeProductContextApi: ProductContextApi | null = null;

function getOrCreateProductContextApi() {
	maybeProductContextApi ??= ProductContextApi.create({});
	return maybeProductContextApi;
}

export const getFeatureFlagPatientContextByPatientIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetFeatureFlagPatientContextRequest,
	GetFeatureFlagPatientContextResponse
>((request) => getOrCreateProductContextApi().getFeatureFlagPatientContext(request));

export const getAllowedTabsByPatientIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetAllowedTabsRequest,
	GetAllowedTabsResponse
>((request) => getOrCreateProductContextApi().getAllowedTabs(request));
